/**
 * FeedbackPanel -- Toolbox slide-out content for suggestions / general feedback.
 *
 * Lighter form than BugReportPanel: category dropdown + description +
 * optional screenshot. No system info / logs required by default. See
 * GitLab issue #86 for the tabbed Toolbox UX this is part of.
 *
 * Submits to POST /api/reports/feedback which creates a GitLab issue
 * with [FEEDBACK] title prefix, `discussion` label, and a `type::*`
 * label derived from the category.
 */

import { useState, useRef, useCallback } from 'react'
import {
  Camera,
  Clipboard,
  Upload,
  Pencil,
  Loader2,
  Send,
  X,
  FileText,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/utils/cn'
import { apiUrl } from '@/lib/api/apiUrl'
import { useBridge } from '@/core/bridge/useBridge'
import { unwrapBridgeResult } from '@/core/bridge/bridgeResult'
import { ScreenshotAnnotator } from './ScreenshotAnnotator'

interface AttachedFile {
  id: string
  name: string
  dataUrl: string
  type: 'screenshot' | 'upload'
}

type Category =
  | 'feature-request'
  | 'ux-improvement'
  | 'general-feedback'
  | 'other'

const CATEGORY_OPTIONS: Array<{ value: Category; label: string }> = [
  { value: 'feature-request', label: 'Feature request' },
  { value: 'ux-improvement', label: 'UX improvement' },
  { value: 'general-feedback', label: 'General feedback' },
  { value: 'other', label: 'Other' },
]

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new Error('FileReader returned non-string result'))
    }
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

async function resolveScreenshotToDataUrl(ref: string): Promise<string> {
  if (ref.startsWith('data:')) return ref
  const response = await fetch(ref, { cache: 'no-store' })
  if (!response.ok) {
    throw new Error(`Failed to load screenshot (${response.status})`)
  }
  const blob = await response.blob()
  return blobToDataUrl(blob)
}

export function FeedbackPanel() {
  const { isConnected, callFunction } = useBridge()

  const [category, setCategory] = useState<Category>('feature-request')
  const [description, setDescription] = useState('')
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [annotatingFile, setAnnotatingFile] = useState<AttachedFile | null>(
    null,
  )

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const idCounter = useRef(0)

  const generateId = useCallback(() => {
    idCounter.current += 1
    return `feedback-${Date.now()}-${idCounter.current}`
  }, [])

  const addFile = useCallback(
    (name: string, dataUrl: string, type: 'screenshot' | 'upload') => {
      setAttachedFiles((prev) => [
        ...prev,
        { id: generateId(), name, dataUrl, type },
      ])
    },
    [generateId],
  )

  const removeFile = useCallback((id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id))
  }, [])

  const handleCaptureEditor = useCallback(async () => {
    if (!isConnected) {
      toast.error('Bridge not connected -- cannot capture screenshot')
      return
    }
    setIsCapturing(true)
    try {
      const result = unwrapBridgeResult(
        await callFunction('GDBCore.CaptureEditorScreenshot'),
      )
      if (!result || typeof result !== 'string') {
        throw new Error('Capture returned no image')
      }
      const dataUrl = await resolveScreenshotToDataUrl(result)
      addFile('Editor Screenshot', dataUrl, 'screenshot')
      toast.success('Screenshot captured')
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Screenshot capture failed'
      toast.error(message)
    } finally {
      setIsCapturing(false)
    }
  }, [isConnected, callFunction, addFile])

  const handlePaste = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read()
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith('image/'))
        if (imageType) {
          const blob = await item.getType(imageType)
          const dataUrl = await blobToDataUrl(blob)
          addFile('Pasted Image', dataUrl, 'screenshot')
          toast.success('Image pasted from clipboard')
          return
        }
      }
      toast.error('No image found in clipboard')
    } catch {
      toast.error('Failed to read clipboard')
    }
  }, [addFile])

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files) return
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          toast.error(`Unsupported file type: ${file.name}`)
          continue
        }
        try {
          const dataUrl = await blobToDataUrl(file)
          addFile(file.name, dataUrl, 'upload')
        } catch {
          toast.error(`Failed to read ${file.name}`)
        }
      }
      e.target.value = ''
    },
    [addFile],
  )

  const handleAnnotationSave = useCallback(
    (annotatedDataUrl: string) => {
      if (!annotatingFile) return
      setAttachedFiles((prev) =>
        prev.map((f) =>
          f.id === annotatingFile.id ? { ...f, dataUrl: annotatedDataUrl } : f,
        ),
      )
      setAnnotatingFile(null)
      toast.success('Annotation saved')
    },
    [annotatingFile],
  )

  const canSubmit = description.trim().length > 0 && !isSubmitting

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      const screenshots = attachedFiles
        .map((f) => f.dataUrl)
        .filter((url) => url.startsWith('data:'))

      const categoryLabel = CATEGORY_OPTIONS.find(
        (c) => c.value === category,
      )?.label ?? 'Feedback'

      const body = {
        title: `${categoryLabel} — ${description.trim().slice(0, 80)}`,
        description: description.trim(),
        category,
        screenshots,
      }

      const response = await fetch(apiUrl('/api/reports/feedback'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          (errorData as Record<string, string>).error ||
            `Submit failed (${response.status})`,
        )
      }

      toast.success('Feedback submitted -- thank you!')
      setDescription('')
      setCategory('feature-request')
      setAttachedFiles([])
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to submit feedback'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [canSubmit, description, category, attachedFiles])

  if (annotatingFile) {
    return (
      <ScreenshotAnnotator
        imageUrl={annotatingFile.dataUrl}
        onSave={handleAnnotationSave}
        onCancel={() => setAnnotatingFile(null)}
      />
    )
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden">
      <div className="flex flex-col gap-3 p-3 flex-1 min-h-0">
        {/* Category */}
        <div className="shrink-0">
          <label
            htmlFor="feedback-category"
            className="block text-ue-text-muted mb-1"
            style={{ fontSize: '10px' }}
          >
            CATEGORY
          </label>
          <select
            id="feedback-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            className={cn(
              'w-full rounded px-2 py-1.5',
              'bg-ue-bg-darkest border border-ue-border-medium',
              'text-ue-text-primary',
              'focus:outline-none focus:border-ue-accent-primary/50',
            )}
            style={{ fontSize: '11px' }}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div className="flex flex-col shrink-0">
          <label
            htmlFor="feedback-description"
            className="block text-ue-text-muted mb-1"
            style={{ fontSize: '10px' }}
          >
            FEEDBACK <span className="text-ue-accent-red">*</span>
          </label>
          <textarea
            id="feedback-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What would make GameDevBuddy better? Suggestions, improvements, ideas — we want to hear it."
            rows={6}
            className={cn(
              'w-full min-h-[120px] resize-y rounded px-2 py-1.5',
              'bg-ue-bg-darkest border border-ue-border-medium',
              'text-ue-text-primary placeholder:text-ue-text-muted',
              'focus:outline-none focus:border-ue-accent-primary/50',
            )}
            style={{ fontSize: '11px' }}
          />
        </div>

        {/* Optional screenshot */}
        <div className="shrink-0">
          <label
            className="block text-ue-text-muted mb-1.5"
            style={{ fontSize: '10px' }}
          >
            SCREENSHOT (OPTIONAL)
          </label>

          <div className="flex gap-1.5 mb-2 flex-wrap">
            <button
              onClick={handleCaptureEditor}
              disabled={!isConnected || isCapturing}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded border transition-colors',
                'border-ue-border-medium',
                isConnected && !isCapturing
                  ? 'text-ue-text-secondary hover:bg-white/5 hover:text-ue-text-primary'
                  : 'text-ue-text-muted cursor-not-allowed opacity-50',
              )}
              style={{ fontSize: '10px' }}
            >
              {isCapturing ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Camera size={12} />
              )}
              Capture
            </button>

            <button
              onClick={handlePaste}
              className="flex items-center gap-1 px-2 py-1 rounded border border-ue-border-medium text-ue-text-secondary hover:bg-white/5 hover:text-ue-text-primary transition-colors"
              style={{ fontSize: '10px' }}
            >
              <Clipboard size={12} />
              Paste
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 px-2 py-1 rounded border border-ue-border-medium text-ue-text-secondary hover:bg-white/5 hover:text-ue-text-primary transition-colors"
              style={{ fontSize: '10px' }}
            >
              <Upload size={12} />
              Upload
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>

          {attachedFiles.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {attachedFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-2 p-1.5 rounded bg-ue-bg-darkest border border-ue-border-medium"
                >
                  {file.dataUrl.startsWith('data:image') ? (
                    <button
                      onClick={() => setAnnotatingFile(file)}
                      className="relative shrink-0 w-10 h-10 rounded overflow-hidden border border-ue-border-medium hover:border-ue-accent-primary/50 transition-colors group"
                      title="Click to annotate"
                    >
                      <img
                        src={file.dataUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Pencil size={10} className="text-white" />
                      </div>
                    </button>
                  ) : (
                    <div className="shrink-0 w-10 h-10 rounded bg-ue-bg-darker border border-ue-border-medium flex items-center justify-center">
                      <FileText size={14} className="text-ue-text-muted" />
                    </div>
                  )}
                  <span
                    className="flex-1 text-ue-text-secondary truncate"
                    style={{ fontSize: '10px' }}
                  >
                    {file.name}
                  </span>
                  <button
                    onClick={() => removeFile(file.id)}
                    className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-ue-text-muted hover:text-ue-accent-red hover:bg-white/5 transition-colors"
                    title="Remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submit */}
      <div className="shrink-0 p-3 border-t border-ue-border-medium">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2 rounded font-medium transition-colors',
            canSubmit
              ? 'bg-ue-accent-primary text-white hover:bg-ue-accent-primary/90'
              : 'bg-ue-bg-darkest text-ue-text-muted cursor-not-allowed',
          )}
          style={{ fontSize: '11px' }}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send size={14} />
              Send Feedback
            </>
          )}
        </button>
      </div>
    </div>
  )
}
